import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { IOption } from '@shared/models';
import { InputsActions, LandingPageActions } from '@state/actions';

import { environment } from 'environments/environment';

export const inputsFeatureKey = 'inputs';

export const defaultData: State = {
  brLower: 0.4,
  brUpper: 0.6,
  entities: {
    1: {
      ballastMass: 300,
      externalArea: 900,
      id: 1,
      internalArea: 300,
      steelMass: 300,
      volume: 1400,
    },
  },
  ids: [1],
  selectedOption: 1,
};

/**
 * @ngrx/entity provides a predefined interface for handling
 * a structured dictionary of records. This interface
 * includes an array of ids, and a dictionary of the provided
 * model type by id. This interface is extended to include
 * any additional interface properties. Used here to select the option by id.
 */
export interface State extends EntityState<IOption> {
  brLower: number;
  brUpper: number;
  selectedOption: 1 | 2 | 3;
}

/**
 * createEntityAdapter creates many an object of helper
 * functions for single or multiple operations
 * against the dictionary of records. The configuration
 * object takes a record id selector function and
 * a sortComparer option which is set to a compare
 * function if the records are to be sorted.
 */
export const adapter: EntityAdapter<IOption> = createEntityAdapter<IOption>({
  sortComparer: false,
});

export const initialState: State = adapter.getInitialState(
  environment.defaultData
    ? {
        brLower: 0.4,
        brUpper: 0.6,
        selectedOption: 1,
      }
    : {
        brLower: null,
        brUpper: null,
        selectedOption: 1,
      }
);

export const reducer = createReducer(
  initialState,
  on(LandingPageActions.clearInputData, () => {
    return {
      ...initialState,
    };
  }),
  on(LandingPageActions.submitDefaults, () => {
    return {
      ...defaultData,
    };
  }),
  on(InputsActions.submitOptions, (state, { inputOptions }) =>
    adapter.setAll(inputOptions, state)
  ),
  on(InputsActions.updateBRBounds, (state, { brLower, brUpper }) => {
    return {
      ...state,
      brLower,
      brUpper,
    };
  })
);

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */

export const selectBRLower = (state: State) => state.brLower;
export const selectBRUpper = (state: State) => state.brUpper;
export const selectOptionId = (state: State) => state.selectedOption;
